export const FORM_SCHEMA_VERSION = 2 as const;

export type FormElementKind = 'QUESTION' | 'CONTENT' | 'PAGE';

export type FormElementType =
  | 'SHORT_TEXT'
  | 'LONG_TEXT'
  | 'NUMBER'
  | 'DATE'
  | 'DATE_TIME'
  | 'SINGLE_CHOICE'
  | 'MULTIPLE_CHOICE'
  | 'YES_NO'
  | 'FILE_UPLOAD'
  | 'EMAIL'
  | 'PHONE'
  | 'URL'
  | 'RATING'
  | 'ACKNOWLEDGEMENT'
  | 'HEADING'
  | 'PARAGRAPH'
  | 'IMAGE'
  | 'ATTACHMENT'
  | 'DIVIDER'
  | 'SECTION'
  | 'PAGE_BREAK';

export type FormConditionOperator =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'CONTAINS'
  | 'IN'
  | 'EMPTY'
  | 'NOT_EMPTY'
  | 'GREATER_THAN'
  | 'GREATER_THAN_OR_EQUAL'
  | 'LESS_THAN'
  | 'LESS_THAN_OR_EQUAL'
  | 'BEFORE'
  | 'AFTER';

export interface FormOption {
  id: string;
  label: string;
  value: string;
}

export interface FormCondition {
  fieldId: string;
  operator: FormConditionOperator;
  value?: unknown;
}

export interface FormConditionGroup {
  match: 'ALL' | 'ANY';
  conditions: FormCondition[];
}

export interface FormElementConfig {
  options?: FormOption[];
  allowOther?: boolean;
  otherLabel?: string;
  otherRequired?: boolean;
  placeholder?: string;
  linkUrl?: string;
  mediaId?: string;
  mediaUrl?: string;
  mediaKey?: string;
  mediaToken?: string;
  mediaName?: string;
  mediaSize?: number;
  mediaMimeType?: string;
  min?: number;
  max?: number;
  step?: number;
  maxLength?: number;
  allowedMimeTypes?: string[];
  maxFiles?: number;
  maxFileSizeBytes?: number;
  emoji?: string;
  textColor?: string;
  backgroundColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  textSize?: 'normal' | 'large' | 'small';
}

export interface FormElement {
  id: string;
  kind: FormElementKind;
  type: FormElementType;
  title?: string;
  description?: string;
  required?: boolean;
  config?: FormElementConfig;
  validation?: Record<string, unknown>;
  visibility?: FormConditionGroup;
  visibilityEffect?: 'SHOW' | 'HIDE';
}

export type FormLogicAction =
  | { type: 'SHOW' | 'HIDE' | 'GO_TO_PAGE'; targetId: string }
  | { type: 'SUBMIT' }
  | { type: 'END_WITH_MESSAGE'; message: string };

export interface FormLogicRule {
  id: string;
  sourcePageId: string;
  when: FormConditionGroup;
  action: FormLogicAction;
}

export interface FormDefinition {
  schemaVersion: typeof FORM_SCHEMA_VERSION;
  title: string;
  description: string;
  elements: FormElement[];
  logic: FormLogicRule[];
  theme?: {
    accentColor?: string;
    backgroundColor?: string;
  };
}

export interface FormAnswer {
  fieldId: string;
  value?: unknown;
  otherValue?: string;
}

export interface FormMediaRef {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  url: string;
  token?: string;
}

export interface FormSubmission {
  formSchemaVersion: typeof FORM_SCHEMA_VERSION;
  formAnswers: FormAnswer[];
}
